import type { ReactNode } from 'react'
import { SessionRefreshProvider } from './attempts-pane'
import { CodeRefProvider } from './code-ref-context'
import { ErrorMarkerProvider } from './error-marker-context'
import { ExecutionProvider } from './execution-context'
import { ProblemProvider } from './problem-context'
import { SessionProvider } from './session-context'
import { TestCasesProvider } from './test-cases-context'
import { TimerProvider } from './timer/timer-context'

interface EditorContextProviderProps {
  problemSlug: string | null
  children: ReactNode
}

/**
 * Composite provider that wraps all editor-related contexts in the correct order.
 * This reduces nesting in the main Editor component and makes context dependencies clearer.
 *
 * Context hierarchy:
 * - CodeRefProvider: Shared ref for live code access (AI pane reads latest code)
 * - ProblemProvider: Loads problem data (statement, examples, test cases)
 * - SessionProvider: Manages code drafts and session persistence
 * - ExecutionProvider: Tracks code execution state (run/submit)
 * - ErrorMarkerProvider: Bridges runtime-error locations from the results pane to Monaco
 * - TimerProvider: Manages stopwatch/timer state with persistence
 * - TestCasesProvider: Manages test case state
 * - SessionRefreshProvider: Provides refresh mechanism for session history
 */
export function EditorContextProvider({
  problemSlug,
  children,
}: EditorContextProviderProps) {
  return (
    <CodeRefProvider problemSlug={problemSlug}>
      <ProblemProvider problemSlug={problemSlug}>
        <SessionProvider problemId={problemSlug}>
          <ExecutionProvider>
            <ErrorMarkerProvider>
              <TimerProvider>
                <TestCasesProvider>
                  <SessionRefreshProvider problemSlug={problemSlug}>
                    {children}
                  </SessionRefreshProvider>
                </TestCasesProvider>
              </TimerProvider>
            </ErrorMarkerProvider>
          </ExecutionProvider>
        </SessionProvider>
      </ProblemProvider>
    </CodeRefProvider>
  )
}
